import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import { Card, CardHeader, CardTitle, CardDescription, CardBody } from '../../ui/Card';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../../../contexts/AuthContext';
import { loadAllGuardsForExport, GuardData } from '../../../pages/manage_guard/service/guardService';

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[6]};

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled(Card)`
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03) !important;
  border: 1px solid ${theme.colors.light.border.light};
  
  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
  }
`;

const ChartTitle = styled(CardTitle)`
  font-size: ${theme.typography.fontSize['2xl']};
  margin-bottom: ${theme.spacing[2]};
  color: ${theme.colors.light.text.primary};
  font-weight: ${theme.typography.fontWeight.bold};
`;

const ChartSubtitle = styled(CardDescription)`
  margin-bottom: ${theme.spacing[6]};
  color: ${theme.colors.light.text.secondary};
  font-size: ${theme.typography.fontSize.base};
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 320px;
  margin-top: ${theme.spacing[4]};
  padding: ${theme.spacing[2]};
`;

const DataPoints = styled.div`
  display: flex;
  gap: ${theme.spacing[6]};
  margin-top: ${theme.spacing[6]};
  padding-top: ${theme.spacing[4]};
  border-top: 1px solid ${theme.colors.light.border.light};
  flex-wrap: wrap;
`;

const DataPoint = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
  font-weight: ${theme.typography.fontWeight.medium};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: ${theme.colors.light.background.tertiary};
  border-radius: ${theme.borderRadius.md};
`;

const DeploymentCharts: React.FC = () => {
  const { companyData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [temporaryData, setTemporaryData] = useState<Array<{ name: string; value: number }>>([]);
  const [permanentData, setPermanentData] = useState<Array<{ name: string; value: number }>>([]);
  const [permanentBarData, setPermanentBarData] = useState<Array<{ day: number; value: number }>>([]);
  const [temporaryCount, setTemporaryCount] = useState(0);
  const [permanentCount, setPermanentCount] = useState(0);
  const [clientTypeData, setClientTypeData] = useState<Array<{ name: string; guards: number }>>([]);

  useEffect(() => {
    const fetchDeploymentData = async () => {
      if (!companyData?.id) {
        setLoading(false);
        return;
      }

      try {
        const guards = await loadAllGuardsForExport(companyData.id);
        
        // Separate temporary and permanent deployments
        const temporaryDeployments = guards.filter((g) => {
          const deployment = g.currentDeployment;
          return deployment && deployment.status === 'temporary';
        });
        
        const permanentDeployments = guards.filter((g) => {
          const deployment = g.currentDeployment;
          return deployment && (!deployment.status || deployment.status === 'permanent');
        });

        setTemporaryCount(temporaryDeployments.length);
        setPermanentCount(permanentDeployments.length);

        // Prepare data for last 6 months
        const now = new Date();
        const monthlyTemporary: Record<string, number> = {};
        const monthlyPermanent: Record<string, number> = {};

        for (let i = 5; i >= 0; i--) {
          const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const monthKey = date.toLocaleString('default', { month: 'short', year: 'numeric' });
          monthlyTemporary[monthKey] = 0;
          monthlyPermanent[monthKey] = 0;
        }

        // Count deployments by month (based on createdAt or postDate)
        temporaryDeployments.forEach((guard) => {
          const deployment = guard.currentDeployment;
          if (deployment?.createdAt) {
            const deployDate = deployment.createdAt?.toDate ? deployment.createdAt.toDate() : new Date(deployment.createdAt);
            const monthKey = deployDate.toLocaleString('default', { month: 'short', year: 'numeric' });
            if (monthlyTemporary.hasOwnProperty(monthKey)) {
              monthlyTemporary[monthKey]++;
            }
          }
        });

        permanentDeployments.forEach((guard) => {
          const deployment = guard.currentDeployment;
          if (deployment?.createdAt) {
            const deployDate = deployment.createdAt?.toDate ? deployment.createdAt.toDate() : new Date(deployment.createdAt);
            const monthKey = deployDate.toLocaleString('default', { month: 'short', year: 'numeric' });
            if (monthlyPermanent.hasOwnProperty(monthKey)) {
              monthlyPermanent[monthKey]++;
            }
          }
        });

        setTemporaryData(
          Object.entries(monthlyTemporary).map(([name, value]) => ({ name, value }))
        );

        setPermanentData(
          Object.entries(monthlyPermanent).map(([name, value]) => ({ name, value }))
        );

        // Prepare daily data for permanent deployments (last 30 days)
        const dailyData: Record<number, number> = {};
        for (let i = 29; i >= 0; i--) {
          dailyData[i] = 0;
        }

        permanentDeployments.forEach((guard) => {
          const deployment = guard.currentDeployment;
          if (deployment?.createdAt) {
            const deployDate = deployment.createdAt?.toDate ? deployment.createdAt.toDate() : new Date(deployment.createdAt);
            const daysAgo = Math.floor((now.getTime() - deployDate.getTime()) / (1000 * 60 * 60 * 24));
            if (daysAgo >= 0 && daysAgo < 30) {
              dailyData[daysAgo]++;
            }
          }
        });

        setPermanentBarData(
          Object.entries(dailyData).map(([day, value]) => ({
            day: parseInt(day) + 1,
            value,
          }))
        );

        // Calculate guards deployed per client type
        const clientTypeCounts: Record<string, number> = {
          Bank: 0,
          Factory: 0,
          Individual: 0,
        };

        guards.forEach((guard) => {
          const deployment = guard.currentDeployment;
          if (deployment?.client) {
            const clientType = deployment.client.type;
            if (clientType === 'Bank' || clientType === 'Factory' || clientType === 'Individual') {
              clientTypeCounts[clientType]++;
            }
          }
        });

        setClientTypeData([
          { name: 'Bank', guards: clientTypeCounts.Bank },
          { name: 'Factory', guards: clientTypeCounts.Factory },
          { name: 'Individual', guards: clientTypeCounts.Individual },
        ]);
      } catch (error) {
        console.error('Error fetching deployment data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDeploymentData();
  }, [companyData]);

  if (loading) {
    return (
      <>
        <ChartCard>
          <CardBody>
            <div style={{ textAlign: 'center', padding: theme.spacing[8] }}>Loading...</div>
          </CardBody>
        </ChartCard>
        <ChartCard>
          <CardBody>
            <div style={{ textAlign: 'center', padding: theme.spacing[8] }}>Loading...</div>
          </CardBody>
        </ChartCard>
      </>
    );
  }

  return (
    <>
      <ChartCard>
        <CardBody>
          <ChartTitle>{temporaryCount} Temporary Deployment</ChartTitle>
          <ChartSubtitle>Number of Temporary Deployment</ChartSubtitle>
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={temporaryData}>
                <defs>
                  <linearGradient id="colorTemporary" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={theme.colors.primary[500]} stopOpacity={0.8} />
                    <stop offset="95%" stopColor={theme.colors.primary[500]} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.light.border.light} />
                <XAxis
                  dataKey="name"
                  stroke={theme.colors.light.text.secondary}
                  tick={{ fill: theme.colors.light.text.secondary }}
                  fontSize={12}
                />
                <YAxis 
                  stroke={theme.colors.light.text.secondary} 
                  tick={{ fill: theme.colors.light.text.secondary }}
                  fontSize={12} 
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme.colors.white,
                    border: `1px solid ${theme.colors.light.border.medium}`,
                    borderRadius: theme.borderRadius.md,
                    boxShadow: theme.shadows.lg,
                    color: theme.colors.light.text.primary,
                  }}
                  labelStyle={{ color: theme.colors.light.text.primary }}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={theme.colors.primary[500]}
                  fillOpacity={1}
                  fill="url(#colorTemporary)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartContainer>
          <DataPoints>
            {temporaryData.map((point, index) => (
              <DataPoint key={index}>
                {point.name}
              </DataPoint>
            ))}
          </DataPoints>
        </CardBody>
      </ChartCard>

      <ChartCard>
        <CardBody>
          <ChartTitle>{permanentCount} Permanent Deployment</ChartTitle>
          <ChartSubtitle>Number of total Guards Deployed.</ChartSubtitle>
          <DataPoints>
            {permanentData.map((point, index) => (
              <DataPoint key={index}>
                {point.value} {point.name}
              </DataPoint>
            ))}
          </DataPoints>
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={permanentBarData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.light.border.light} />
                <XAxis
                  dataKey="day"
                  stroke={theme.colors.light.text.secondary}
                  fontSize={12}
                  tick={{ fill: theme.colors.light.text.secondary }}
                />
                <YAxis 
                  stroke={theme.colors.light.text.secondary} 
                  tick={{ fill: theme.colors.light.text.secondary }}
                  fontSize={12} 
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme.colors.white,
                    border: `1px solid ${theme.colors.light.border.medium}`,
                    borderRadius: theme.borderRadius.md,
                    boxShadow: theme.shadows.lg,
                    color: theme.colors.light.text.primary,
                  }}
                  labelStyle={{ color: theme.colors.light.text.primary }}
                />
                <Bar
                  dataKey="value"
                  fill={theme.colors.primary[500]}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardBody>
      </ChartCard>
    </>
  );
};

export default DeploymentCharts;

